<script setup lang="ts">
  import AiReasoningSparklesIcon from '@/components/agent-actions/ai-assistant-beta/icons/AiReasoningSparklesIcon.vue'
  import { Button } from '@/components/ui/button'
  import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
  import { useTranslation } from '@/composables/useTranslation'
  import { AgentActionsService, RatingNegativeVariantEnum } from '@/types/generated'
  import { Check, ChevronDown, ThumbsDown, ThumbsUp, X } from 'lucide-vue-next'
  import { computed, ref, watch } from 'vue'

  type ReasoningTranslation = { en?: string; de?: string; fr?: string }

  function parseReasoningTranslation(raw: unknown): ReasoningTranslation | null {
    if (raw == null) return null
    if (typeof raw === 'string') {
      const trimmed = raw.trim()
      if (!trimmed) return null
      try {
        const parsed = JSON.parse(trimmed) as unknown
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          return parsed as ReasoningTranslation
        }
      } catch {
        return null
      }
      return null
    }
    if (typeof raw === 'object' && !Array.isArray(raw)) {
      return raw as ReasoningTranslation
    }
    return null
  }

  function reasoningHasLocalizedText(
    t10n: ReasoningTranslation | null | undefined,
    loc: string
  ): boolean {
    if (!t10n) return false
    const localized = t10n[loc as keyof ReasoningTranslation] ?? t10n.de ?? t10n.en ?? t10n.fr
    return Boolean(typeof localized === 'string' && localized.trim())
  }

  type Step = 'reasoning' | 'feedback' | 'feedback-success'
  const props = defineProps<{
    /**
     * UUID of the Action that wrote the AI value. Required for rating;
     * if null, the popover still shows the reasoning but hides rating controls.
     */
    actionUuid?: string | null
    /**
     * Locale-aware reasoning from the Action row (preferred). Mirrors what
     * AgentActionWidget reads off `action.reasoning_translation`.
     */
    reasoningTranslation?: ReasoningTranslation | null
    /**
     * Plain-text fallback from the augmentation row itself (e.g. when an upstream
     * extraction step set it). Used when reasoningTranslation is missing.
     */
    explanation?: string
    /**
     * Initial rating state, if the action was already rated in a previous session.
     */
    initialRating?: 'positive' | 'negative' | null
    /**
     * Lazy mode: when set together with ``actionType``, the badge fetches the
     * latest committed action of that type for this case the first time the
     * popover opens. Used in list views where eager-loading per row would be
     * wasteful. Eager-mode callers (detail page) pass ``actionUuid`` directly
     * and don't need these.
     */
    caseUuid?: string
    actionType?: string
  }>()

  const { t, locale } = useTranslation({
    en: {
      autoFilled: 'Auto-filled',
      reasoningTitle: 'AI reasoning',
      noReasoning: 'No reasoning provided.',
      thumbsUpAria: 'This was helpful',
      thumbsDownAria: "This wasn't right",
      feedbackTitle: "What didn't fit?",
      feedbackSubtitle:
        'Choose a reason. You can optionally add a short note about what should be improved.',
      reasonPlaceholder: 'Choose a reason',
      commentPlaceholder: 'Add more details',
      submit: 'Send feedback',
      cancel: 'Cancel',
      close: 'Close',
      thanks: 'Feedback sent!',
      thanksSubtitle: 'Thank you for your feedback!',
      reasonInstructionsNotFollowed: 'Instructions not followed',
      reasonPayloadNotCorrect: 'Wrong value',
      reasonActionNotNeeded: 'Action not needed',
      reasonOther: 'Other'
    },
    de: {
      autoFilled: 'Automatisch ausgefüllt',
      reasoningTitle: 'KI-Begründung',
      noReasoning: 'Keine Begründung verfügbar.',
      thumbsUpAria: 'Das hat gepasst',
      thumbsDownAria: 'Das hat nicht gepasst',
      feedbackTitle: 'Was hat nicht gepasst?',
      feedbackSubtitle:
        'Wählen Sie einen Grund aus. Optional können Sie kurz ergänzen, was verbessert werden sollte.',
      reasonPlaceholder: 'Grund auswählen',
      commentPlaceholder: 'Weitere Details ergänzen',
      submit: 'Feedback senden',
      cancel: 'Abbrechen',
      close: 'Schließen',
      thanks: 'Feedback gesendet!',
      thanksSubtitle: 'Vielen Dank für Ihr Feedback!',
      reasonInstructionsNotFollowed: 'Anweisungen nicht befolgt',
      reasonPayloadNotCorrect: 'Falscher Wert',
      reasonActionNotNeeded: 'Aktion nicht nötig',
      reasonOther: 'Anderer Grund'
    },
    fr: {
      autoFilled: 'Rempli automatiquement',
      reasoningTitle: "Justification de l'IA",
      noReasoning: 'Aucune justification fournie.',
      thumbsUpAria: 'Cela convenait',
      thumbsDownAria: 'Cela ne convenait pas',
      feedbackTitle: "Qu'est-ce qui n'allait pas ?",
      feedbackSubtitle:
        'Choisissez une raison. Vous pouvez ajouter une brève note sur ce qui devrait être amélioré.',
      reasonPlaceholder: 'Choisir une raison',
      commentPlaceholder: 'Ajouter des détails',
      submit: 'Envoyer',
      cancel: 'Annuler',
      close: 'Fermer',
      thanks: 'Feedback envoyé !',
      thanksSubtitle: 'Merci pour votre retour !',
      reasonInstructionsNotFollowed: 'Instructions non respectées',
      reasonPayloadNotCorrect: 'Mauvaise valeur',
      reasonActionNotNeeded: 'Action non nécessaire',
      reasonOther: 'Autre raison'
    }
  })

  const isOpen = ref(false)
  const step = ref<Step>('reasoning')
  const rating = ref<'positive' | 'negative' | null>(props.initialRating ?? null)
  watch(
    () => props.initialRating,
    (next) => {
      rating.value = next ?? null
    }
  )
  const isSubmitting = ref(false)
  const negativeVariant = ref<string>('')
  const negativeComment = ref('')
  const isReasonSelectOpen = ref(false)

  // Lazy-fetched state. Populated by ``ensureActionLoaded()`` on first popover
  // open when the parent passed ``caseUuid + actionType`` instead of an action
  // UUID. ``effective*`` computeds prefer these over the eager props.
  const lazyActionUuid = ref<string | null>(null)
  const lazyReasoningTranslation = ref<ReasoningTranslation | null>(null)
  const isLoadingAction = ref(false)
  /** Lazy list fetch (``caseUuid`` + ``actionType``, no ``actionUuid``). */
  const hasFetched = ref(false)
  /** Single-action retrieve when ``actionUuid`` is set but reasoning is missing. */
  const retrieveAttempted = ref(false)

  const effectiveActionUuid = computed(() => lazyActionUuid.value ?? props.actionUuid ?? null)
  const propReasoningNormalized = computed(() =>
    parseReasoningTranslation(props.reasoningTranslation)
  )
  const effectiveReasoningTranslation = computed(
    () => lazyReasoningTranslation.value ?? propReasoningNormalized.value ?? null
  )

  const badgeRatingIdentityKey = computed(
    () => props.actionUuid ?? `lazy:${props.caseUuid ?? ''}:${props.actionType ?? ''}`
  )

  watch(badgeRatingIdentityKey, (next, prev) => {
    if (next === prev) return
    hasFetched.value = false
    retrieveAttempted.value = false
    lazyActionUuid.value = null
    lazyReasoningTranslation.value = null
    rating.value = props.initialRating ?? null
  })

  // Prefer the action's translated reasoning over the augmentation's raw explanation —
  // the action carries locale-aware copy from ReasoningTranslationAgent, while
  // augmentation.explanation may be empty or upstream-extraction text.
  const displayedReasoning = computed(() => {
    const t10n = effectiveReasoningTranslation.value
    if (t10n) {
      const localized =
        t10n[locale.value as keyof ReasoningTranslation] ?? t10n.de ?? t10n.en ?? t10n.fr
      if (localized) return localized
    }
    return props.explanation ?? ''
  })

  const applyRatingFromAction = (action: { rating?: string | number | null }) => {
    const ratingRaw = action.rating
    if (ratingRaw === null || ratingRaw === undefined) return
    const numeric = parseFloat(String(ratingRaw))
    if (!Number.isNaN(numeric)) {
      rating.value = numeric >= 2.5 ? 'positive' : 'negative'
    }
  }

  const ensureActionLoaded = async () => {
    // List rows: resolve uuid + reasoning via latest committed action for case+type.
    if (!props.actionUuid && props.caseUuid && props.actionType) {
      if (hasFetched.value) return
      const capturedCaseUuid = props.caseUuid
      const capturedActionType = props.actionType
      hasFetched.value = true
      isLoadingAction.value = true
      try {
        const response = await AgentActionsService.agentActionsList({
          caseUuid: capturedCaseUuid,
          actionType: capturedActionType,
          isCommitted: true,
          ordering: '-committed_at',
          limit: 1
        })
        if (props.caseUuid !== capturedCaseUuid || props.actionType !== capturedActionType) return
        const action = response.results?.[0]
        if (!action) return
        lazyActionUuid.value = action.uuid ?? null
        const t10n = parseReasoningTranslation(
          (action as { reasoning_translation?: unknown }).reasoning_translation
        )
        if (t10n) lazyReasoningTranslation.value = t10n
        applyRatingFromAction(action as { rating?: string | number | null })
      } catch (error) {
        console.error('[KiBadge] failed to lazy-fetch action', error)
      } finally {
        isLoadingAction.value = false
      }
      return
    }

    // Case list passes ``actionUuid`` from annotations but reasoning may be absent or
    // not shaped like a locale map — hydrate from the action detail once.
    if (!props.actionUuid || retrieveAttempted.value) return
    if (props.explanation?.trim()) return
    if (reasoningHasLocalizedText(lazyReasoningTranslation.value, locale.value)) return
    if (reasoningHasLocalizedText(propReasoningNormalized.value, locale.value)) return

    retrieveAttempted.value = true
    const capturedUuid = props.actionUuid
    isLoadingAction.value = true
    try {
      const action = await AgentActionsService.agentActionsRetrieve({ uuid: capturedUuid })
      if (props.actionUuid !== capturedUuid) return
      const t10n = parseReasoningTranslation(
        (action as { reasoning_translation?: unknown }).reasoning_translation
      )
      if (t10n) lazyReasoningTranslation.value = t10n
      applyRatingFromAction(action as { rating?: string | number | null })
    } catch (error) {
      console.error('[KiBadge] failed to retrieve action for reasoning', error)
    } finally {
      isLoadingAction.value = false
    }
  }

  const reasonOptions = computed(() => [
    {
      value: RatingNegativeVariantEnum.INSTRUCTIONS_NOT_FOLLOWED,
      label: t('reasonInstructionsNotFollowed')
    },
    { value: RatingNegativeVariantEnum.PAYLOAD_NOT_CORRECT, label: t('reasonPayloadNotCorrect') },
    { value: RatingNegativeVariantEnum.ACTION_NOT_NEEDED, label: t('reasonActionNotNeeded') },
    { value: RatingNegativeVariantEnum.OTHER, label: t('reasonOther') }
  ])

  const canRate = computed(
    () => Boolean(effectiveActionUuid.value) && !rating.value && !isSubmitting.value
  )
  const selectedReasonLabel = computed(
    () => reasonOptions.value.find((option) => option.value === negativeVariant.value)?.label ?? ''
  )

  const resetState = () => {
    step.value = 'reasoning'
    negativeVariant.value = ''
    negativeComment.value = ''
  }

  const handleOpenChange = (next: boolean) => {
    isOpen.value = next
    if (next) {
      void ensureActionLoaded()
    } else {
      resetState()
    }
  }

  const handleClose = () => {
    isOpen.value = false
    resetState()
  }

  const handleThumbsUp = async () => {
    const uuid = effectiveActionUuid.value
    if (!uuid || !canRate.value) return
    isSubmitting.value = true
    try {
      await AgentActionsService.agentActionsRateCreate({
        requestBody: { agent_action_uuid: uuid, rating: '5.0' }
      })
      rating.value = 'positive'
      step.value = 'feedback-success'
    } catch (error) {
      console.error('[KiBadge] thumbs up failed', error)
    } finally {
      isSubmitting.value = false
    }
  }

  const handleThumbsDown = () => {
    if (!effectiveActionUuid.value || !canRate.value) return
    step.value = 'feedback'
  }

  const submitNegative = async () => {
    const uuid = effectiveActionUuid.value
    if (!uuid || !negativeVariant.value || isSubmitting.value) return
    isSubmitting.value = true
    try {
      await AgentActionsService.agentActionsRateCreate({
        requestBody: {
          agent_action_uuid: uuid,
          rating: '0.0',
          rating_negative_variant: negativeVariant.value as RatingNegativeVariantEnum,
          rating_negative_comment: negativeComment.value || undefined
        }
      })
      rating.value = 'negative'
      step.value = 'feedback-success'
    } catch (error) {
      console.error('[KiBadge] negative rating failed', error)
    } finally {
      isSubmitting.value = false
    }
  }

  const cancelFeedback = () => {
    step.value = 'reasoning'
    negativeVariant.value = ''
    negativeComment.value = ''
    isReasonSelectOpen.value = false
  }

  const selectNegativeReason = (value: string) => {
    negativeVariant.value = value
    isReasonSelectOpen.value = false
  }
</script>

<template>
  <Popover
    :open="isOpen"
    @update:open="handleOpenChange"
  >
    <PopoverTrigger as-child>
      <button
        type="button"
        class="inline-flex h-5 min-w-5 items-center justify-center rounded bg-[#F3E8FF] px-1 py-0.5 text-xs
          font-medium leading-4 text-[#9333EA] transition-colors hover:bg-[#E9D5FF] focus-visible:outline-none
          focus-visible:ring-2 focus-visible:ring-[#C084FC] focus-visible:ring-offset-1"
        :aria-label="t('autoFilled')"
        data-cy="ki-badge"
      >
        KI
      </button>
    </PopoverTrigger>
    <!-- Radix collision avoidance can flip the popover away from the badge; keep it in-flow with the viewport. -->
    <PopoverContent
      align="start"
      :avoid-collisions="false"
      update-position-strategy="always"
      class="max-h-[min(560px,calc(100dvh-80px))] w-72 overflow-y-auto overflow-x-hidden rounded-xl border
        border-[#E5E7EB] bg-white p-0 pb-4
        shadow-[0px_4px_8px_0px_rgba(0,0,0,0.08),0px_8px_12px_0px_rgba(0,0,0,0.08)]"
    >
      <!-- Header -->
      <div class="flex h-14 items-center justify-between bg-white py-2.5 pl-4 pr-2">
        <div
          v-if="step === 'reasoning'"
          class="flex items-center gap-1"
        >
          <AiReasoningSparklesIcon />
          <span class="text-xs font-medium leading-4 text-[#9333EA]">
            {{ t('autoFilled') }}
          </span>
        </div>
        <div v-else></div>
        <Button
          variant="ghost"
          size="icon"
          class="h-9 w-9 rounded-2xl bg-white text-[#4B5563] hover:bg-[#F9FAFB] hover:text-[#1F2937]"
          :aria-label="t('close')"
          @click="handleClose"
        >
          <X class="h-3 w-3" />
        </Button>
      </div>

      <!-- Body -->
      <div
        v-if="step === 'reasoning'"
        class="flex flex-col items-center gap-2 px-4"
      >
        <div class="self-stretch text-sm font-medium leading-5 text-[#4B5563]">
          {{ t('reasoningTitle') }}
        </div>
        <div
          class="self-stretch text-sm font-normal leading-5 text-[#4B5563]"
          :class="{ italic: !displayedReasoning && !isLoadingAction }"
        >
          <template v-if="isLoadingAction && !displayedReasoning">…</template>
          <template v-else>{{ displayedReasoning || t('noReasoning') }}</template>
        </div>
        <div
          v-if="effectiveActionUuid || isLoadingAction"
          class="flex w-full items-center gap-1.5 pt-1"
        >
          <Button
            variant="ghost"
            size="icon"
            class="h-8 w-8 rounded-md text-[#6B7280] hover:bg-[#F3E8FF] hover:text-[#9333EA]"
            :class="{ 'bg-[#F3E8FF] text-[#9333EA]': rating === 'positive' }"
            :disabled="!canRate"
            :aria-label="t('thumbsUpAria')"
            data-cy="ki-badge-thumbs-up"
            @click="handleThumbsUp"
          >
            <ThumbsUp class="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            class="h-8 w-8 rounded-md text-[#6B7280] hover:bg-[#F3E8FF] hover:text-[#9333EA]"
            :class="{ 'bg-[#F3E8FF] text-[#9333EA]': rating === 'negative' }"
            :disabled="!canRate"
            :aria-label="t('thumbsDownAria')"
            data-cy="ki-badge-thumbs-down"
            @click="handleThumbsDown"
          >
            <ThumbsDown class="h-4 w-4" />
          </Button>
        </div>
      </div>

      <!-- Feedback form -->
      <div
        v-else-if="step === 'feedback'"
        class="flex flex-col items-center gap-2 px-4"
      >
        <div class="self-stretch text-sm font-medium leading-5 text-[#4B5563]">
          {{ t('feedbackTitle') }}
        </div>
        <div class="self-stretch text-sm font-normal leading-5 text-[#4B5563]">
          {{ t('feedbackSubtitle') }}
        </div>
        <div class="flex w-full flex-col gap-3 pt-1">
          <div class="relative">
            <button
              type="button"
              class="flex h-10 w-full items-center gap-1.5 overflow-hidden rounded-lg border border-[#E5E7EB] bg-white
                px-2.5 py-2 text-left shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)] focus:outline-none focus:ring-1
                focus:ring-[#C084FC]"
              data-cy="ki-badge-feedback-reason"
              @click="isReasonSelectOpen = !isReasonSelectOpen"
            >
              <span
                class="flex-1 text-sm font-normal leading-5"
                :class="negativeVariant ? 'text-[#4B5563]' : 'text-[#9CA3AF]'"
              >
                {{ selectedReasonLabel || t('reasonPlaceholder') }}
              </span>
              <ChevronDown class="h-4 w-4 text-[#1F2937]" />
            </button>
            <div
              v-if="isReasonSelectOpen"
              class="absolute left-0 right-0 top-[calc(100%+4px)] z-20 overflow-hidden rounded-lg border border-[#E5E7EB]
                bg-white shadow-[0px_4px_8px_0px_rgba(0,0,0,0.08)]"
            >
              <button
                v-for="opt in reasonOptions"
                :key="opt.value"
                type="button"
                class="flex w-full items-center px-2.5 py-2 text-left text-sm font-normal leading-5 text-[#4B5563]
                  hover:bg-[#F9FAFB]"
                :class="{ 'bg-[#F3E8FF] text-[#9333EA]': negativeVariant === opt.value }"
                @click="selectNegativeReason(opt.value)"
              >
                {{ opt.label }}
              </button>
            </div>
          </div>
          <textarea
            v-model="negativeComment"
            class="h-24 resize-none rounded-lg border border-[#E5E7EB] bg-white px-2.5 py-2 text-sm font-normal
              leading-5 text-[#4B5563] shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)] focus:outline-none focus:ring-1
              focus:ring-[#C084FC]"
            :placeholder="t('commentPlaceholder')"
            data-cy="ki-badge-feedback-comment"
          ></textarea>
        </div>
        <div class="flex w-full flex-col gap-3 bg-gradient-to-b from-white/0 to-white pt-6">
          <Button
            class="h-11 w-full rounded-lg bg-gradient-to-l from-[#9333EA] to-[#C084FC] text-sm font-medium leading-5
              text-white hover:from-[#7E22CE] hover:to-[#9333EA]"
            :disabled="!negativeVariant || isSubmitting"
            data-cy="ki-badge-feedback-submit"
            @click="submitNegative"
          >
            {{ t('submit') }}
          </Button>
          <Button
            variant="secondary"
            class="h-11 w-full rounded-lg border border-[#E5E7EB] bg-white text-sm font-medium leading-5 text-[#1F2937]"
            :disabled="isSubmitting"
            @click="cancelFeedback"
          >
            {{ t('cancel') }}
          </Button>
        </div>
      </div>

      <!-- Success state -->
      <div
        v-else
        class="flex min-h-[210px] flex-col gap-9 px-4"
      >
        <div class="flex flex-1 flex-col items-center justify-center gap-3">
          <Check class="h-6 w-6 text-[#1F2937]" />
          <div class="flex w-full flex-col items-center gap-1">
            <div class="text-center text-sm font-medium leading-5 text-[#4B5563]">
              {{ t('thanks') }}
            </div>
            <div class="text-center text-sm font-normal leading-5 text-[#4B5563]">
              {{ t('thanksSubtitle') }}
            </div>
          </div>
        </div>
        <Button
          variant="secondary"
          class="h-11 w-full rounded-lg border border-[#E5E7EB] bg-white text-sm font-medium leading-5 text-[#1F2937]"
          @click="handleClose"
        >
          {{ t('close') }}
        </Button>
      </div>
    </PopoverContent>
  </Popover>
</template>
